import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, useTheme } from '@mui/material';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import BASE_URL from '../../API/config';

const CoinSlider = ({ isMobile }) => {
  const theme = useTheme();
  const [coins, setCoins] = useState([]);
  const [error, setError] = useState(null);

  const fetchCoins = async () => {
    try {
      const response = await fetch(`${BASE_URL}/coins/hot`);
      if (!response.ok) throw new Error('Failed to fetch hot coins from backend');
      const data = await response.json();
      setCoins(data);
      setError(null);
    } catch (error) {
      console.error('Error fetching hot coins from backend:', error.message);
      if (!coins.length) setError(error.message || 'Failed to fetch hot coins from backend');
    }
  };

  useEffect(() => {
    fetchCoins();
    const interval = setInterval(fetchCoins, 5000);
    return () => clearInterval(interval);
  }, []);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 6,
    slidesToScroll: 6,
    autoplay: true,
    autoplaySpeed: 5000,
    arrows: false,
    responsive: [
      { breakpoint: 1200, settings: { slidesToShow: 4, slidesToScroll: 4 } },
      { breakpoint: 600, settings: { slidesToShow: 3, slidesToScroll: 3 } },
      { breakpoint: 400, settings: { slidesToShow: 2, slidesToScroll: 2 } },
    ],
  };

  return (
    <Box
      sx={{
        px: { xs: 1, sm: 2 },
        mb: { xs: 1, sm: 2 },
        bgcolor: theme.palette.background.slider,
        py: 2,
        borderRadius: 1,
      }}
    >
      {error && !coins.length ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography
            variant="h6"
            sx={{ color: theme.palette.text.primary, fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' } }}
          >
            {error}
          </Typography>
        </Box>
      ) : coins.length > 0 ? (
        <Slider {...sliderSettings}>
          {coins.map((coin, index) => (
            <Box key={index} sx={{ px: { xs: 0.5, sm: 1 } }}>
              <Card
                sx={{
                  bgcolor: theme.palette.background.card,
                  color: theme.palette.text.primary,
                  borderRadius: 2,
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  '&:hover': { transform: 'scale(1.05)', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)' },
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: { xs: 1, sm: 1.5, md: 2 }, px: { xs: 1, sm: 1.5 } }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                      color: theme.palette.text.primary,
                      lineHeight: 1.2,
                    }}
                  >
                    {coin.symbol}/USDT
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      my: { xs: 0.5, sm: 1 },
                      fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                      fontWeight: 'bold',
                      color: theme.palette.text.secondary,
                      lineHeight: 1.3,
                    }}
                  >
                    {coin.price}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: coin.change < 0 ? theme.palette.error.main : theme.palette.success.main,
                      fontWeight: 'medium',
                      fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    {coin.change}%
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Slider>
      ) : null}
    </Box>
  );
};

export default CoinSlider;